import { StepKey } from "@/src/lib/workflows/steps/types";
import { TaskStatus } from "@/src/models/task";
import { type TaskStep, TaskStepStatus } from "@/src/models/task-step";

const MANUAL_STEP_CONTINUATION_STATUSES: ReadonlySet<TaskStatus> = new Set([
  TaskStatus.AWAITING_INFORMATION,
  TaskStatus.FAILED,
  TaskStatus.REVIEW_NEEDED,
  TaskStatus.VERIFICATION_NEEDED,
] satisfies readonly TaskStatus[]);

export function isTprEligibilityTimeoutReview(step: TaskStep): boolean {
  return (
    step.stepKey === StepKey.SUBMIT_ATHENA_INSURANCE_ELIGIBILITY &&
    step.status === TaskStepStatus.COMPLETED &&
    step.output?.outcome === "unknown" &&
    step.output?.reviewReason === "polling_timeout"
  );
}

/**
 * The row the task is stopped on and that Continue restarts the run from: the last row of a FAILED
 * task, or a timed-out eligibility check under review. Told apart from failures the run went past
 * without step config, so the timeline and the endpoint always agree.
 */
export function isTprStoppedOnStep(taskStatus: TaskStatus, taskSteps: TaskStep[], step: TaskStep): boolean {
  if (taskStatus === TaskStatus.REVIEW_NEEDED && isTprEligibilityTimeoutReview(step)) return true;
  if (taskStatus !== TaskStatus.FAILED || step.status !== TaskStepStatus.FAILED) return false;

  return taskSteps.every((row) => row.stepIndex <= step.stepIndex);
}

/** A failed row the run carried on past. It is red, but nothing is waiting on it. */
export function isTprContinuedFailure(taskStatus: TaskStatus, taskSteps: TaskStep[], step: TaskStep): boolean {
  return step.status === TaskStepStatus.FAILED && !isTprStoppedOnStep(taskStatus, taskSteps, step);
}

export type TprManualStepContinuationPlan =
  | { type: "rejected"; reason: string }
  | {
      type: "continue";
      step: TaskStep;
      update: Pick<TaskStep, "status" | "output">;
      /**
       * True only for the row the task is stopped on. Settling a failure the run went past never
       * restarts the run: whatever the task is waiting on now, it isn't that row.
       */
      resume: boolean;
    };

export function planTprManualStepContinuation({
  taskStatus,
  taskSteps,
  stepKey,
}: {
  taskStatus: TaskStatus;
  taskSteps: TaskStep[];
  stepKey: StepKey;
}): TprManualStepContinuationPlan {
  if (!MANUAL_STEP_CONTINUATION_STATUSES.has(taskStatus)) {
    return { type: "rejected", reason: "Task is not awaiting manual continuation" };
  }

  const step = taskSteps.find((row) => row.stepKey === stepKey);

  if (!step) {
    return { type: "rejected", reason: "The selected step is not awaiting recheck or manual continuation" };
  }

  if (isTprStoppedOnStep(taskStatus, taskSteps, step)) {
    return { type: "continue", step, update: resolveTprManualStepContinuation(step), resume: true };
  }

  if (isTprContinuedFailure(taskStatus, taskSteps, step)) {
    return { type: "continue", step, update: completedWithoutFailure(step), resume: false };
  }

  return { type: "rejected", reason: "The selected step is not awaiting recheck or manual continuation" };
}

/** Eligibility needs an Athena result; manual continuation must rerun it rather than skip it. */
function resolveTprManualStepContinuation(step: TaskStep): Pick<TaskStep, "status" | "output"> {
  if (step.stepKey === StepKey.SUBMIT_ATHENA_INSURANCE_ELIGIBILITY) {
    // PAUSED resumes at this step. Clear the old polling window so a timed-out check can start fresh.
    return { status: TaskStepStatus.PAUSED, output: null };
  }

  return completedWithoutFailure(step);
}

/**
 * Once COMPLETED a row's output reaches later steps, so it must not carry the failure it recorded as
 * if that were the step's result.
 */
function completedWithoutFailure(step: TaskStep): Pick<TaskStep, "status" | "output"> {
  const { error: _error, failureReason: _failureReason, ccErrors: _ccErrors, ...output } = step.output ?? {};
  const completedOutput = step.stepKey === StepKey.CREATE_COVERAGE ? { ...output, manuallyCompleted: true } : output;

  return {
    status: TaskStepStatus.COMPLETED,
    output: Object.keys(completedOutput).length ? completedOutput : null,
  };
}
